import React from 'react';
import ArticleLayout, { FaqList, SectionHeading, linkClassName } from '../components/ArticleLayout';

// Date shown on the page and used in the Article structured data (src/seo.js).
// Update it whenever the guide's content changes.
export const GUIA_UPDATED = '2026-10-05';

export const GUIA_REQUISITOS = [
  { title: 'Formato PDF', description: 'El documento debe ser un archivo PDF.' },
  { title: 'Resolución de 300 DPI', description: 'Cada página debe estar digitalizada a 300 puntos por pulgada.' },
  { title: 'Escala de grises', description: 'Las imágenes deben estar en escala de grises de 8 bits, no a color ni en blanco y negro puro.' },
  { title: 'Tamaño máximo de 3 MB', description: 'Si tu documento pesa más, divídelo en varios archivos.' },
  { title: 'Sin contraseña', description: 'El PDF no debe estar protegido ni cifrado.' },
  { title: 'Sin elementos activos', description: 'No debe contener formularios rellenables, JavaScript ni archivos u objetos incrustados.' },
  { title: 'Sin páginas en blanco', description: 'Elimina las hojas vacías antes de subir el archivo.' },
];

export const GUIA_PASOS = [
  {
    title: 'Abre el convertidor',
    description: 'Entra al Convertidor PDF VUCEM de EC-Compliance desde tu navegador. No necesitas instalar nada ni crear una cuenta.',
  },
  {
    title: 'Selecciona tu PDF',
    description: 'Arrastra el archivo al recuadro o haz clic para elegirlo desde tu equipo.',
  },
  {
    title: 'Completa la verificación',
    description: 'Marca la verificación de seguridad que aparece debajo del recuadro.',
  },
  {
    title: 'Convierte y descarga',
    description: 'Haz clic en "Convertir". En unos segundos se descarga tu archivo con el sufijo _VUCEM.pdf, a 300 DPI y en escala de grises.',
  },
  {
    title: 'Súbelo a VUCEM',
    description: 'Ingresa a VUCEM, digitaliza el documento con el archivo convertido y obtén tu e-document.',
  },
];

export const GUIA_ERRORES = [
  {
    title: 'El archivo está a color',
    description: 'Un escaneo a color no cumple con el formato. El convertidor lo pasa a escala de grises automáticamente.',
  },
  {
    title: 'La resolución no es de 300 DPI',
    description: 'Muchos escáneres guardan a 150 o 200 DPI por omisión. El convertidor ajusta cada página a 300 DPI.',
  },
  {
    title: 'El archivo es demasiado pesado',
    description: 'Si el PDF convertido supera el límite, divide el documento en partes y digitaliza cada una por separado.',
  },
  {
    title: 'El PDF tiene contraseña',
    description: 'Quita la protección desde el programa que generó el archivo antes de convertirlo.',
  },
  {
    title: 'El escaneo original es ilegible',
    description: 'Subir la resolución no recupera texto borroso. Si el documento original no se lee bien, vuelve a escanearlo.',
  },
];

export const GUIA_FAQS = [
  {
    question: '¿Qué formato pide VUCEM para digitalizar documentos?',
    answer: 'VUCEM pide archivos PDF a 300 DPI, en escala de grises de 8 bits, sin contraseña, sin formularios ni elementos incrustados y sin páginas en blanco y con un tamaño máximo de 3 MB por archivo.',
  },
  {
    question: '¿El convertidor PDF VUCEM es gratis?',
    answer: 'Sí. El convertidor es gratuito, funciona en el navegador y no requiere registro.',
  },
  {
    question: '¿Guardan mi archivo después de convertirlo?',
    answer: 'No. El archivo se procesa para la conversión y se descarta al terminar.',
  },
  {
    question: '¿Qué es un e-document?',
    answer: 'Es el número que VUCEM asigna a cada documento digitalizado. Se declara en el pedimento para vincular el documento con la operación de comercio exterior.',
  },
  {
    question: '¿Puedo convertir varios archivos a la vez?',
    answer: 'El convertidor procesa un archivo a la vez. Si digitalizas documentos todos los días, EC Traffic, nuestra plataforma, los convierte, los transmite a VUCEM y guarda los e-documents en el expediente de cada pedimento.',
  },
];

const GuiaVucemPage = () => (
  <ArticleLayout
    path="/como-convertir-pdf-vucem"
    title="Cómo convertir un PDF al formato VUCEM"
    updated={GUIA_UPDATED}
    intro={
      <>
        Para digitalizar un documento en VUCEM, el PDF debe estar a 300 DPI y en escala de grises, sin contraseña y con
        un tamaño máximo de 3 MB. La forma más rápida de lograrlo es subir tu archivo a nuestro{' '}
        <a href="/convertidor" className={linkClassName}>convertidor PDF VUCEM gratuito</a>: en unos segundos descargas
        el archivo listo para obtener tu e-document.
      </>
    }
    ctaTitle="¿Digitalizas documentos para VUCEM todos los días?"
    ctaText="EC Traffic convierte tus documentos, los transmite a VUCEM, obtiene tus e-documents y los guarda en el expediente de cada pedimento."
  >
    <SectionHeading id="requisitos">Requisitos de VUCEM para documentos digitalizados</SectionHeading>
    <p className="mt-4 text-gray-600 dark:text-gray-400">
      La ventanilla única de comercio exterior rechaza los archivos que no cumplen con este formato:
    </p>
    <ul className="mt-6 grid gap-4 sm:grid-cols-2">
      {GUIA_REQUISITOS.map((requisito) => (
        <li
          key={requisito.title}
          className="rounded-lg border border-gray-300 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900"
        >
          <h3 className="font-semibold text-gray-900 dark:text-gray-100">{requisito.title}</h3>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{requisito.description}</p>
        </li>
      ))}
    </ul>

    <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
      Desde el 5 de mayo de 2026, VUCEM es sustituida por la Ventanilla Única de Trámites de Comercio Exterior (VUTCE),
      administrada por la ATDT. En el sector se le sigue llamando VUCEM.
    </p>

    <SectionHeading id="paso-a-paso">Paso a paso para convertir tu PDF</SectionHeading>
    <ol className="mt-6 space-y-6">
      {GUIA_PASOS.map((paso, index) => (
        <li key={paso.title} className="flex gap-4">
          <span
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-700 text-sm font-semibold text-white dark:bg-teal-500 dark:text-gray-900"
            aria-hidden="true"
          >
            {index + 1}
          </span>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">{paso.title}</h3>
            <p className="mt-1 text-gray-600 dark:text-gray-400">{paso.description}</p>
          </div>
        </li>
      ))}
    </ol>
    <div className="mt-8 flex justify-center">
      <a
        href="/convertidor"
        className="inline-flex h-10 items-center justify-center rounded-md bg-indigo-700 px-8 text-sm font-medium text-white shadow transition-colors hover:bg-indigo-800 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-indigo-700 dark:bg-teal-500 dark:text-gray-900 dark:hover:bg-teal-600 dark:focus-visible:ring-teal-300"
      >
        Convertir mi PDF ahora
      </a>
    </div>

    <SectionHeading id="errores-comunes">Errores comunes y cómo evitarlos</SectionHeading>
    <dl className="mt-6 space-y-4">
      {GUIA_ERRORES.map((error) => (
        <div key={error.title} className="rounded-lg border border-gray-300 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900">
          <dt className="font-semibold text-gray-900 dark:text-gray-100">{error.title}</dt>
          <dd className="mt-1 text-sm text-gray-600 dark:text-gray-400">{error.description}</dd>
        </div>
      ))}
    </dl>

    <SectionHeading id="preguntas-frecuentes">Preguntas frecuentes</SectionHeading>
    <FaqList faqs={GUIA_FAQS} />
  </ArticleLayout>
);

export default GuiaVucemPage;
